// src/components/profile/ProfileCard.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  Chip,
  IconButton,
  Button,
  Badge,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from '@mui/material';
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
  Verified as VerifiedIcon,
  PhotoCamera as PhotoCameraIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const ProfileCard = () => {
  const { user, updateProfile, loading } = useAuth();
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoURL, setPhotoURL] = useState('');
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState(null);

  // Función para obtener las iniciales del nombre
  const getInitials = () => {
    const name = user?.displayName || user?.email || 'Usuario';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Función para obtener el color del rol
  const getRoleColor = (role) => {
    const roleColors = {
      admin: 'error',
      organizador: 'primary',
      cronometrador: 'secondary',
      entrenador: 'info',
      atleta: 'success',
      juez: 'warning',
      transmision: 'error',
      locutor: 'primary',
      prensa: 'secondary',
      fotografo: 'info'
    };
    return roleColors[role] || 'default';
  };

  // Función para obtener el nombre del rol
  const getRoleName = (role) => {
    const roleNames = {
      admin: 'Administrador',
      organizador: 'Organizador',
      cronometrador: 'Cronometrador',
      entrenador: 'Entrenador',
      atleta: 'Atleta',
      juez: 'Juez',
      transmision: 'Transmisión',
      locutor: 'Locutor',
      prensa: 'Prensa',
      fotografo: 'Fotógrafo'
    };
    return roleNames[role] || role;
  };

  // Función para actualizar la foto de perfil
  const handleUpdatePhoto = async () => {
    if (!photoURL.trim()) return;

    setUpdating(true);
    setMessage(null);

    try {
      await updateProfile({ photoURL: photoURL.trim() });
      setMessage({ type: 'success', text: 'Foto actualizada correctamente' });
      setPhotoDialogOpen(false);
      setPhotoURL('');
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setUpdating(false);
    }
  };

  // Función para formatear fecha
  const formatDate = (date) => {
    if (!date) return 'No disponible';
    
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return dateObj.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'No disponible';
    }
  };

  const userRole = user?.rol || user?.role;

  return (
    <>
      <Card 
        elevation={2} 
        sx={{ 
          height: 'fit-content',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ textAlign: 'center', pb: 4 }}>
          {/* Avatar con badge para cambiar foto */}
          <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <IconButton
                  size="small"
                  onClick={() => setPhotoDialogOpen(true)}
                  sx={{
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  <PhotoCameraIcon fontSize="small" />
                </IconButton>
              }
            >
              <Avatar
                src={user?.photoURL}
                sx={{
                  width: 120,
                  height: 120,
                  border: '4px solid white',
                  fontSize: '2rem',
                  bgcolor: 'rgba(255,255,255,0.2)'
                }}
              >
                {!user?.photoURL && getInitials()}
              </Avatar>
            </Badge>

            {/* Indicador de verificación */}
            {user?.emailVerified && (
              <VerifiedIcon
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  color: 'success.main',
                  bgcolor: 'white',
                  borderRadius: '50%',
                  fontSize: 24
                }}
              />
            )}
          </Box>

          {/* Nombre y rol */}
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {user?.displayName || user?.email?.split('@')[0] || 'Usuario'}
          </Typography>

          {userRole && (
            <Chip
              label={getRoleName(userRole)}
              color={getRoleColor(userRole)}
              sx={{ 
                mb: 2,
                fontWeight: 'bold',
                bgcolor: 'rgba(255,255,255,0.9)',
                color: 'text.primary'
              }}
            />
          )}

          <Typography variant="body2" sx={{ opacity: 0.9, mb: 3 }}>
            {user?.email}
          </Typography>
        </CardContent>

        {/* Información adicional */}
        <Box sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <EmailIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Email"
                secondary={user?.email}
              />
              {user?.emailVerified && (
                <VerifiedIcon color="success" fontSize="small" />
              )}
            </ListItem>

            <Divider />

            <ListItem>
              <ListItemIcon>
                <BadgeIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Rol"
                secondary={userRole ? getRoleName(userRole) : 'No asignado'}
              />
            </ListItem>

            <Divider />

            <ListItem>
              <ListItemIcon>
                <CalendarIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Miembro desde"
                secondary={formatDate(user?.createdAt)}
              />
            </ListItem>

            {user?.lastLoginAt && (
              <>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Último acceso"
                    secondary={formatDate(user?.lastLoginAt)}
                  />
                </ListItem>
              </>
            )}
          </List>
        </Box>

        {/* Mensaje de estado */}
        {message && (
          <Box sx={{ p: 2 }}>
            <Alert severity={message.type} size="small">
              {message.text}
            </Alert>
          </Box>
        )}
      </Card>

      {/* Dialog para cambiar foto */}
      <Dialog open={photoDialogOpen} onClose={() => setPhotoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PhotoCameraIcon sx={{ mr: 1 }} />
            Cambiar Foto de Perfil
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="URL de la imagen"
            placeholder="https://ejemplo.com/mi-foto.jpg"
            value={photoURL}
            onChange={(e) => setPhotoURL(e.target.value)}
            sx={{ mt: 2 }}
            helperText="Ingresa la URL de tu nueva foto de perfil"
          />
          
          {photoURL && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Vista previa:
              </Typography>
              <Avatar
                src={photoURL}
                sx={{ width: 80, height: 80, margin: '0 auto' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleUpdatePhoto}
            variant="contained"
            disabled={!photoURL.trim() || updating}
          >
            {updating ? 'Actualizando...' : 'Actualizar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfileCard;